import type { RevenueOverview } from '@growth/client';
import { useParams } from 'react-router';
import { ColumnChart, LineChart } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { AssumptionsNote, BasisToggle, useMoneyParams } from '../components/money.tsx';
import { Card, ChartCard, DataTable, Empty, ErrorBox, StatTile } from '../components/ui.tsx';
import { describeFilters, useGlobalFilters } from '../filters.ts';
import { fmtCompact, fmtDay, fmtInt, fmtMoney, fmtNum, fmtPct } from '../format.ts';
import { ciText } from '../metrics.ts';
import { useApi } from '../session.tsx';

const FORMAT_LABEL: Record<string, string> = {
  rewarded: 'Rewarded',
  interstitial: 'Intersticial',
  banner: 'Banner',
  app_open: 'App open',
  rewarded_interstitial: 'Rewarded intersticial',
  native: 'Nativo',
  unknown: 'Desconocido',
};
const formatLabel = (f: string) => FORMAT_LABEL[f] ?? f;

function noteEs(n: string): string {
  let m = /^Revenue in (.+) has no exchange rate to (\w+) yet/.exec(n);
  if (m) return `Los ingresos en ${m[1]} aún no tienen tipo de cambio a ${m[2]} y quedan fuera (job sync-fx: BCE + fuente de respaldo para monedas que el BCE no publica).`;
  m = /^Ads are shown but no ad_revenue events arrive/.exec(n);
  if (m) return 'Se muestran anuncios pero no llegan eventos ad_revenue: conecta el callback de ingresos del SDK de anuncios (AdMob OnAdPaid) a GrowthAds.Revenue.';
  m = /^Rewarded opt-in needs ad_available events/.exec(n);
  if (m) return 'El opt-in de rewarded necesita eventos ad_available (GrowthAds.Available cuando se ofrece el anuncio).';
  return n;
}

export function MonetizationPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const money = useMoneyParams();
  const r = useApi<RevenueOverview>(`rev:${gameId}:${filters.key}:${money.basis}`, (api, s) => api.revenueOverview(gameId, { ...filters.params, basis: money.basis }, s));
  const d = r.data;
  const t = d?.totals;
  const ccy = d?.currency ?? 'EUR';
  const stale = r.loading && !!d;
  const daily = d?.daily ?? [];
  const iapKey = money.basis === 'net' ? 'iap_net' : 'iap_gross';
  const basisLabel = money.basis === 'net' ? 'neto estimado' : 'bruto';

  return (
    <>
      <PageHeader title="Monetización" sub={`Ingresos por anuncios (SDK de anuncios, por impresión) y compras (IAP), en ${ccy} · ${basisLabel}`} right={<BasisToggle value={money.basis} onChange={money.setBasis} />} />
      <FilterBar filters={filters} compare={false} />
      {r.error && <ErrorBox error={r.error} onRetry={r.reload} />}
      {d?.notes.map((n) => (
        <div key={n} className="notice">
          {noteEs(n)}
        </div>
      ))}

      <div className={`kpis loading-frame${stale ? ' stale' : ''}`}>
        <StatTile label="Ingresos" value={fmtMoney(t?.revenue, ccy)} sub={t ? `anuncios ${fmtPct(t.ad_share, 0)} · ${basisLabel}` : undefined} trend={daily.map((x) => x.revenue ?? 0)} />
        <StatTile label="Anuncios" value={fmtMoney(t?.ad_revenue, ccy)} sub={t ? `${fmtCompact(t.impressions)} impresiones · eCPM ${fmtMoney(t.ecpm, ccy)}` : undefined} trend={daily.map((x) => x.ad_revenue ?? 0)} />
        <StatTile label="Compras (IAP)" value={fmtMoney(money.basis === 'net' ? t?.iap_net : t?.iap_gross, ccy)} sub={t ? `bruto ${fmtMoney(t.iap_gross, ccy)} · ${fmtInt(t.purchases)} compras` : undefined} />
        <StatTile label="ARPDAU" value={fmtMoney(t?.arpdau, ccy, 3)} sub={t ? `anuncios ${fmtMoney(t.ad_arpdau, ccy, 3)} · IAP ${fmtMoney(t.iap_arpdau, ccy, 3)}` : undefined} trend={daily.map((x) => x.arpdau ?? 0)} />
        <StatTile label="ARPU" value={fmtMoney(t?.arpu, ccy, 3)} sub={t ? `${fmtInt(t.active_users)} usuarios activos · anuncios ${fmtMoney(t.ad_arpu, ccy, 3)}` : undefined} />
        <StatTile label="ARPPU" value={fmtMoney(t?.arppu, ccy)} sub={t ? `${fmtInt(t.payers)} pagadores · ticket medio ${fmtMoney(t.avg_purchase, ccy)}` : undefined} />
        <StatTile label="Conversión a pagador" value={fmtPct(t?.payer_conversion.rate, 2)} sub={t ? `${ciText(t.payer_conversion)} · primera compra ${fmtPct(t.first_payer_conversion.rate, 2)}` : undefined} />
        <StatTile label="Pagadores que repiten" value={fmtPct(t?.repeat_payer_rate.rate)} sub={t ? `${fmtInt(t.repeat_payer_rate.k)} de ${fmtInt(t.repeat_payer_rate.n)} (≥ 2 compras)` : undefined} />
        <StatTile
          label="Impresiones por DAU"
          value={fmtNum(t?.impressions_per_dau, 2)}
          sub={t ? `rewarded ${fmtNum(t.rewarded_impressions_per_dau, 2)} · intersticial ${fmtNum(t.interstitial_impressions_per_dau, 2)}` : undefined}
        />
        <StatTile
          label="Opt-in de rewarded"
          value={t?.rewarded_opt_in ? fmtPct(t.rewarded_opt_in.rate) : '—'}
          sub={t ? (t.rewarded_opt_in ? `${fmtInt(t.rewarded_opt_in.started)} de ${fmtInt(t.rewarded_opt_in.offered)} ofertas · ${fmtPct(t.rewarded_engagement.rate)} de DAU ven uno` : 'faltan eventos ad_available') : undefined}
        />
      </div>

      <div className="grid cols-2">
        <ChartCard
          title="Ingresos por día"
          subtitle={`Anuncios + compras (${basisLabel}) en ${ccy}${describeFilters(filters.params) ? ` · ${describeFilters(filters.params)}` : ''}`}
          stale={stale}
          table={{
            columns: [{ label: 'Día' }, { label: 'Anuncios', numeric: true }, { label: 'IAP', numeric: true }, { label: 'Total', numeric: true }, { label: 'DAU', numeric: true }, { label: 'ARPDAU', numeric: true }],
            rows: daily.map((x) => [x.date, fmtMoney(x.ad_revenue, ccy), fmtMoney(x[iapKey], ccy), fmtMoney(x.revenue, ccy), fmtInt(x.dau), fmtMoney(x.arpdau, ccy, 4)]),
          }}
        >
          <ColumnChart
            ariaLabel="Ingresos por día"
            x={daily.map((x) => x.date)}
            formatX={(v) => fmtDay(String(v))}
            formatY={(v) => fmtMoney(v, ccy, 0)}
            formatValue={(v) => fmtMoney(v, ccy)}
            totalLabel="Total"
            series={[
              { key: 'ads', label: 'Anuncios', slot: 1, values: daily.map((x) => x.ad_revenue ?? 0) },
              { key: 'iap', label: 'Compras', slot: 2, values: daily.map((x) => x[iapKey] ?? 0) },
            ]}
          />
        </ChartCard>
        <ChartCard
          title="ARPDAU por día"
          subtitle="Ingresos del día ÷ usuarios activos ese día"
          stale={stale}
          table={{
            columns: [{ label: 'Día' }, { label: 'ARPDAU', numeric: true }, { label: 'ARPDAU anuncios', numeric: true }, { label: 'Impresiones / DAU', numeric: true }],
            rows: daily.map((x) => [x.date, fmtMoney(x.arpdau, ccy, 4), fmtMoney(x.ad_arpdau, ccy, 4), fmtNum(x.impressions_per_dau, 2)]),
          }}
        >
          <LineChart
            ariaLabel="ARPDAU por día"
            x={daily.map((x) => x.date)}
            formatX={(v) => fmtDay(String(v))}
            formatY={(v) => fmtMoney(v, ccy, 3)}
            formatValue={(v) => fmtMoney(v, ccy, 4)}
            series={[
              { key: 'arpdau', label: 'ARPDAU', slot: 1, values: daily.map((x) => x.arpdau) },
              { key: 'ad', label: 'Anuncios', slot: 2, values: daily.map((x) => x.ad_arpdau) },
            ]}
          />
        </ChartCard>
      </div>

      <div className="grid cols-2">
        <Card title="Anuncios por formato" subtitle="Impresiones = impresiones pagadas que informa el SDK de anuncios (una por evento ad_revenue).">
          {d && d.ads.by_format.length === 0 ? (
            <Empty>Sin ingresos por anuncios en el periodo.</Empty>
          ) : (
            <DataTable
              data={{
                columns: [{ label: 'Formato' }, { label: 'Ingresos', numeric: true }, { label: '% anuncios', numeric: true }, { label: 'Impresiones', numeric: true }, { label: 'Impr. / DAU', numeric: true }, { label: 'eCPM', numeric: true }, { label: 'ARPDAU', numeric: true }],
                rows: (d?.ads.by_format ?? []).map((x) => [formatLabel(x.ad_format), fmtMoney(x.revenue, ccy), fmtPct(x.share, 0), fmtInt(x.impressions), fmtNum(x.impressions_per_dau, 2), fmtMoney(x.ecpm, ccy), fmtMoney(x.arpdau, ccy, 4)]),
              }}
            />
          )}
          <p className="small muted" style={{ margin: '6px 0 0' }}>
            Fill rate: no disponible (necesita los informes de mediación de AdMob). Los fallos de carga y de visualización salen de ad_failed.
          </p>
        </Card>
        <Card title="Red que sirvió el anuncio" subtitle="Fuente del anuncio dentro de la mediación (ad_network del evento).">
          <DataTable
            data={{
              columns: [{ label: 'Red' }, { label: 'Ingresos', numeric: true }, { label: '%', numeric: true }, { label: 'Impresiones', numeric: true }, { label: 'eCPM', numeric: true }],
              rows: (d?.ads.by_network ?? []).map((x) => [x.ad_network, fmtMoney(x.revenue, ccy), fmtPct(x.share, 0), fmtInt(x.impressions), fmtMoney(x.ecpm, ccy)]),
            }}
          />
        </Card>
      </div>

      <Card title="Ubicaciones de anuncios" subtitle="Ofrecidos = ad_available (oportunidad mostrada al jugador) · opt-in = empezados ÷ ofrecidos · finalización = completados ÷ empezados.">
        <div className={`loading-frame${stale ? ' stale' : ''}`}>
          <DataTable
            data={{
              columns: [
                { label: 'Ubicación' },
                { label: 'Formato' },
                { label: 'Ingresos', numeric: true },
                { label: 'eCPM', numeric: true },
                { label: 'Ofrecidos', numeric: true },
                { label: 'Empezados', numeric: true },
                { label: 'Opt-in', numeric: true },
                { label: 'Finalización', numeric: true },
                { label: 'Fallos al mostrar', numeric: true },
                { label: 'Jugadores', numeric: true },
              ],
              rows: (d?.ads.by_placement ?? []).map((x) => [
                x.placement || '—',
                formatLabel(x.ad_format),
                fmtMoney(x.revenue, ccy),
                fmtMoney(x.ecpm, ccy),
                fmtInt(x.offered),
                fmtInt(x.started),
                fmtPct(x.opt_in),
                fmtPct(x.completion),
                fmtPct(x.show_failure),
                fmtInt(x.users),
              ]),
            }}
          />
        </div>
      </Card>

      <div className="grid cols-2">
        <Card title="Compras por producto" subtitle="Deduplicadas por id de transacción; no cuentan las que fallan la validación del recibo.">
          {d && d.iap.by_product.length === 0 ? (
            <Empty>Sin compras en el periodo.</Empty>
          ) : (
            <DataTable
              data={{
                columns: [{ label: 'Producto' }, { label: 'Compras', numeric: true }, { label: 'Pagadores', numeric: true }, { label: 'Bruto', numeric: true }, { label: 'Neto est.', numeric: true }, { label: 'Ticket medio', numeric: true }, { label: '%', numeric: true }],
                rows: (d?.iap.by_product ?? []).map((x) => [x.product_id, fmtInt(x.purchases), fmtInt(x.payers), fmtMoney(x.gross, ccy), fmtMoney(x.net, ccy), fmtMoney(x.avg_price, ccy), fmtPct(x.share, 0)]),
              }}
            />
          )}
        </Card>
        <Card title="Por país" subtitle="País del jugador el día del ingreso.">
          <DataTable
            data={{
              columns: [{ label: 'País' }, { label: 'Ingresos', numeric: true }, { label: '%', numeric: true }, { label: 'ARPDAU', numeric: true }, { label: 'Pagadores', numeric: true }, { label: 'Conversión', numeric: true }],
              rows: (d?.by_country ?? []).slice(0, 25).map((x) => [x.country, fmtMoney(x.revenue, ccy), fmtPct(x.share, 0), fmtMoney(x.arpdau, ccy, 4), fmtInt(x.payers), fmtPct(x.payer_conversion.rate, 2)]),
            }}
          />
        </Card>
      </div>
      {t && t.missing_fx.rows === 0 && (t.currencies.iap.length > 1 || t.currencies.ads.some((c) => c !== ccy)) && (
        <p className="small muted">
          Monedas: anuncios {t.currencies.ads.join(', ') || '—'} · compras {t.currencies.iap.join(', ') || '—'}; convertidas a {ccy} con el tipo de cada día.
        </p>
      )}
      <AssumptionsNote a={d?.assumptions} />
    </>
  );
}
